import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getCatalogProductsByIds } from '../../../../data/epos';
import { useQuery } from '@tanstack/react-query';
import { getPromotionDetails } from '../../../../data/promotions';
import { getPromotionSwapData, setPromotionSwapData, clearPromotionSwapData } from '../utils/promotionHelpers';
import {
    getPromotionLineStockCap,
    buildPromotionMaxGetBlockedDialogState,
} from '../utils/promotionLineStock';
import { getMissingCompulsoryProductCodes } from '../utils/compulsoryProducts';
import {
    capQuantityToPurchaseLimit,
    clampToPromotionProductMax,
    mergeOrderLimitFieldsFromCartLines,
    validateMaximumOrderQuantityForLineQty,
    getMaximumOrderQuantityFromItem,
    getCartProductQuantityTotal,
    buildPurchaseLimitExceededMessage,
    enrichPromotionBuyProduct,
    EMPTY_SALES_LIMIT_DIALOG,
    buildStockLimitDialogState,
    buildPurchaseLimitDialogState,
} from '../../common/maximumOrderQuantity';
import {
    calculateMaxAllowedGetQuantity as calculateMaxAllowedGetQuantityFromMaps,
    getPromotionOfferProductUnitPrice,
    buildPromotionCartQuantityMaps,
    findPromotionCartLine,
    findPromotionCartLineIndex,
    resolveMandatoryGetMinQuantity,
    getCartItemLookupMap,
    isCompulsoryOfferProduct,
} from '../utils/promotionOfferProductsShared';

export const usePromotionDrawerEditEpos = (
    open,
    promotion,
    onClose,
    cartItems,
    customerIdProp,
    orderId,
    onCartUpdate,
    onQueuePromotionCheck,
    onCheckPromotionGetSwap = null
) => {
    const [activeTab, setActiveTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBuyGroupId, setSelectedBuyGroupId] = useState(null);
    const [selectedGetGroupId, setSelectedGetGroupId] = useState(null);
    const [productQuantities, setProductQuantities] = useState({});
    const [productPricingTypes, setProductPricingTypes] = useState({});
    const [existingCartQuantities, setExistingCartQuantities] = useState({});
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [limitDialog, setLimitDialog] = useState(EMPTY_SALES_LIMIT_DIALOG);
    const [swapData, setSwapData] = useState({ removed: [], added: [] });
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [isSubmittingSwap, setIsSubmittingSwap] = useState(false);
    const [showDiscardDialog, setShowDiscardDialog] = useState(false);
    const [showInvalidProductsDialog, setShowInvalidProductsDialog] = useState(false);
    const [showSwapErrorDialog, setShowSwapErrorDialog] = useState(false);
    const [swapErrorMessage, setSwapErrorMessage] = useState('');
    const [showCompulsoryDialog, setShowCompulsoryDialog] = useState(false);
    const [compulsoryDialogCodes, setCompulsoryDialogCodes] = useState([]);
    const [hasAcknowledgedCompulsoryDialog, setHasAcknowledgedCompulsoryDialog] = useState(false);
    const lastUIQuantitiesRef = useRef({});

    const customerId = customerIdProp ?? cartItems?.[0]?.customer_id ?? null;
    const orderChannel = localStorage.getItem("user_role") === "20" ? "5" : "4";

    const openStockLimitDialog = useCallback((product, capFloor) => {
        setLimitDialog(
            buildStockLimitDialogState(
                {
                    product_name: product?.product_name || 'Product',
                    product_code: product?.product_code || product?.code || '',
                },
                capFloor
            )
        );
    }, []);

    const openPurchaseLimitDialog = useCallback((product, message) => {
        setLimitDialog(
            buildPurchaseLimitDialogState(
                {
                    product_name: product?.product_name || 'Product',
                    product_code: product?.product_code || product?.code || '',
                },
                message
            )
        );
    }, []);

    const closeLimitDialog = useCallback(() => {
        setLimitDialog(EMPTY_SALES_LIMIT_DIALOG);
    }, []);

    const openMaxGetBlockedDialog = useCallback((product) => {
        setLimitDialog(buildPromotionMaxGetBlockedDialogState(product));
    }, []);

    const resolveProductGroupId = (product) => {
        const g =
            product?.group_id ??
            product?.group_no ??
            product?.promoGroupId ??
            product?.promo_group_id;
        if (g === null || g === undefined) return null;
        const s = String(g).trim();
        return s === '' ? null : s;
    };

    const { data: promotionData, isLoading, error, refetch: refetchPromotionDetails } = useQuery({
        queryKey: ['promotionDetails', promotion?.id, customerId],
        queryFn: () => getPromotionDetails(promotion?.id, { customerId, channels: orderChannel }),
        enabled: !!promotion?.id && open,
        staleTime: 0,
        cacheTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (open && promotion?.id) {
            setActiveTab(0);
            setSearchTerm('');
            setSelectedBuyGroupId(null);
            setSelectedGetGroupId(null);
            setProductQuantities({});
            setProductPricingTypes({});
            setExistingCartQuantities({});
            lastUIQuantitiesRef.current = {};
            const savedSwapData = getPromotionSwapData(promotion.id);
            setSwapData(savedSwapData);
            setHasAcknowledgedCompulsoryDialog(false);
            refetchPromotionDetails();
        }
    }, [open, promotion?.id, refetchPromotionDetails]);

    useEffect(() => {
        if (promotion?.id && open && cartItems && Array.isArray(cartItems) && cartItems.length > 0 && promotionData?.data) {
            const promotionIdStr = String(promotion.id);
            const promoDetails = promotionData.data;
            const { existingQuantities, pricingTypes } = buildPromotionCartQuantityMaps(
                cartItems,
                promoDetails,
                promotionIdStr,
                { eposOfferProductLogic: true },
            );
            const quantities = { ...existingQuantities };

            const hasBuyProducts = Object.keys(quantities).some(key => 
                key.startsWith('buy_') && !key.startsWith('get_') && quantities[key] > 0
            );

            const newQuantities = {};
            Object.keys(quantities).forEach(key => {
                if (quantities[key] > 0) {
                    if (hasBuyProducts || !key.startsWith('get_')) {
                        newQuantities[key] = quantities[key];
                    }
                }
            });

            setProductQuantities(newQuantities);
            lastUIQuantitiesRef.current = { ...newQuantities };
            setExistingCartQuantities(existingQuantities);
            setProductPricingTypes(prev => ({ ...prev, ...pricingTypes }));
        } else if (open && promotion?.id) {
            setProductQuantities({});
            setExistingCartQuantities({});
            setProductPricingTypes({});
            lastUIQuantitiesRef.current = {};
        }
    }, [cartItems, promotion?.id, open, promotionData]);

    const promotionDetails = promotionData?.data || {};
    const cartLineItemsForLimits = useMemo(
        () => (Array.isArray(cartItems) ? cartItems : []),
        [cartItems]
    );
    const cartItemLookup = useMemo(
        () => getCartItemLookupMap(cartLineItemsForLimits),
        [cartLineItemsForLimits]
    );
    const buyProductIdsForCatalog = useMemo(
        () =>
            (promotionDetails.buy_products || [])
                .map((p) => p?.product_id)
                .filter((id) => id !== undefined && id !== null && String(id).trim() !== ''),
        [promotionDetails.buy_products]
    );
    const { data: catalogProductsForLimits = [] } = useQuery({
        queryKey: ['promotionDrawerCatalogLimits', buyProductIdsForCatalog.join(',')],
        queryFn: () => getCatalogProductsByIds(buyProductIdsForCatalog),
        enabled: open && buyProductIdsForCatalog.length > 0,
        staleTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
    });
    const catalogByProductId = useMemo(() => {
        const map = new Map();
        (catalogProductsForLimits || []).forEach((row) => {
            const pid = String(row?.product_id ?? row?.id ?? '').trim();
            if (pid) map.set(pid, row);
        });
        return map;
    }, [catalogProductsForLimits]);
    const buyProductsWithLimits = useMemo(
        () =>
            (promotionDetails.buy_products || []).map((product) =>
                enrichPromotionBuyProduct(
                    product,
                    catalogByProductId.get(String(product?.product_id ?? '').trim()),
                    cartLineItemsForLimits
                )
            ),
        [promotionDetails.buy_products, catalogByProductId, cartLineItemsForLimits]
    );
    const buyProductGroupIds = useMemo(() => {
        const products = promotionDetails.buy_products || [];
        const seen = new Set();
        const ordered = [];
        products.forEach((p) => {
            const gid = resolveProductGroupId(p);
            if (gid !== null && !seen.has(gid)) {
                seen.add(gid);
                ordered.push(gid);
            }
        });
        ordered.sort((a, b) => {
            const na = Number(a);
            const nb = Number(b);
            if (!Number.isNaN(na) && !Number.isNaN(nb) && String(na) === String(a) && String(nb) === String(b)) {
                return na - nb;
            }
            return String(a).localeCompare(String(b), undefined, { numeric: true });
        });
        return ordered;
    }, [promotionDetails.buy_products]);

    const getProductGroupIds = useMemo(() => {
        const products = promotionDetails.get_products || [];
        const seen = new Set();
        const ordered = [];
        products.forEach((p) => {
            const gid = resolveProductGroupId(p);
            if (gid !== null && !seen.has(gid)) {
                seen.add(gid);
                ordered.push(gid);
            }
        });
        ordered.sort((a, b) => {
            const na = Number(a);
            const nb = Number(b);
            if (!Number.isNaN(na) && !Number.isNaN(nb) && String(na) === String(a) && String(nb) === String(b)) {
                return na - nb;
            }
            return String(a).localeCompare(String(b), undefined, { numeric: true });
        });
        return ordered;
    }, [promotionDetails.get_products]);

    useEffect(() => {
        if (!open) return;
        if (buyProductGroupIds.length === 0) {
            setSelectedBuyGroupId(null);
            return;
        }
        setSelectedBuyGroupId((prev) =>
            prev != null && buyProductGroupIds.some((id) => String(id) === String(prev))
                ? prev
                : buyProductGroupIds[0]
        );
    }, [open, promotion?.id, buyProductGroupIds]);

    useEffect(() => {
        if (!open) return;
        if (getProductGroupIds.length === 0) {
            setSelectedGetGroupId(null);
            return;
        }
        setSelectedGetGroupId((prev) =>
            prev != null && getProductGroupIds.some((id) => String(id) === String(prev))
                ? prev
                : getProductGroupIds[0]
        );
    }, [open, promotion?.id, getProductGroupIds]);

    const hasGetProducts = (promotionDetails.get_products || []).length > 0;
    const getQuantity = parseInt(promotionDetails?.get_quantity || 0, 10);

    const calculateMaxAllowedGetQuantity = useCallback(() => {
        return calculateMaxAllowedGetQuantityFromMaps(
            promotionDetails,
            existingCartQuantities,
            productQuantities
        );
    }, [promotionDetails, existingCartQuantities, productQuantities]);

    const selectedBuyProductsCount = new Set(
        Object.keys(productQuantities)
            .filter(key => key.startsWith('buy_') && productQuantities[key] > 0)
            .map(key => key.split('_')[1])
    ).size;
    
    const selectedGetProductsCount = new Set(
        Object.keys(productQuantities)
            .filter(key => key.startsWith('get_') && productQuantities[key] > 0)
            .map(key => key.split('_')[1])
    ).size;

    const currentPromoId = promotion?.id ? String(promotion.id) : null;
    const hasCurrentPromotionInCart = currentPromoId && cartItems
        ? cartItems.some(item => {
            const itemPromoId = item.promotion_id ? String(item.promotion_id) : null;
            return itemPromoId === currentPromoId;
        })
        : false;

    const hasOtherPromotionsInCart = currentPromoId && cartItems
        ? cartItems.some(item => {
            const itemPromoId = item.promotion_id ? String(item.promotion_id) : null;
            return itemPromoId !== null && itemPromoId !== currentPromoId;
        })
        : false;

    const shouldLockOfferProducts = hasOtherPromotionsInCart && !hasCurrentPromotionInCart;

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    useEffect(() => {
        if (!hasGetProducts && activeTab === 1) {
            setActiveTab(0);
        }
    }, [hasGetProducts, activeTab]);

    const getProductPrice = getPromotionOfferProductUnitPrice;

    const showPromotionProductLimitDialog = useCallback(
        (product, attemptedQty, pricingType) => {
            if (!product) return;
            const unifiedCap = clampToPromotionProductMax(
                product,
                cartItems,
                pricingType,
                attemptedQty,
                getPromotionLineStockCap
            );
            if (!unifiedCap.wasCapped) return;
            if (unifiedCap.limitType === 'stock' && unifiedCap.stockMax != null) {
                openStockLimitDialog(product, unifiedCap.stockMax);
            } else {
                openPurchaseLimitDialog(
                    product,
                    unifiedCap.purchaseMessage ||
                        buildPurchaseLimitExceededMessage(
                            product,
                            unifiedCap.purchaseMax ?? unifiedCap.maxAllowed
                        )
                );
            }
        },
        [cartItems, openStockLimitDialog, openPurchaseLimitDialog]
    );

    const notifyQuantityLimitExceeded = useCallback(
        (productId, attemptedQty, tabType, pricingType) => {
            const finalPricingType = pricingType || 'Box';
            if (tabType === 'buy') {
                const product = buyProductsWithLimits.find(
                    (p) => String(p.product_id) === String(productId)
                );
                showPromotionProductLimitDialog(product, attemptedQty, finalPricingType);
                return;
            }
            if (tabType !== 'get') return;
            const product = (promotionDetails.get_products || []).find(
                (p) => String(p.product_id) === String(productId)
            );
            const lineStockCap = product
                ? getPromotionLineStockCap(product, finalPricingType)
                : null;
            if (lineStockCap === null) return;
            const capFloor = Math.max(0, Math.floor(lineStockCap));
            if (attemptedQty > capFloor) {
                openStockLimitDialog(product, capFloor);
            }
        },
        [
            buyProductsWithLimits,
            promotionDetails.get_products,
            showPromotionProductLimitDialog,
            openStockLimitDialog,
        ]
    );

    const handleQuantityChange = (productId, value, tabType, pricingType, options = {}) => {
        let quantity =
            typeof value === "number" && Number.isFinite(value)
                ? value
                : (() => {
                    const n = parseFloat(value);
                    return Number.isFinite(n) ? n : 0;
                })();
        
        const product = tabType === 'buy' 
            ? buyProductsWithLimits.find(p => String(p.product_id) === String(productId))
            : (promotionDetails.get_products || []).find(p => String(p.product_id) === String(productId));
        
        const finalPricingType = pricingType || 'Box';
        const uniqueKey = `${tabType}_${productId}_${finalPricingType}`;
        
        const isCompulsory = isCompulsoryOfferProduct(product);
        const mandatoryMinQty =
            tabType === 'get' && isCompulsory && product
                ? resolveMandatoryGetMinQuantity(
                      product,
                      promotionDetails,
                      existingCartQuantities,
                      productQuantities,
                      {
                          cartItems: cartLineItemsForLimits,
                          promotionIdStr: currentPromoId || '',
                      }
                  )
                : 0;
        const existingQty = existingCartQuantities[uniqueKey] || 0;

        if (tabType === 'buy' && quantity > 0 && product) {
            const unifiedCap = clampToPromotionProductMax(
                product,
                cartItems,
                finalPricingType,
                quantity,
                getPromotionLineStockCap
            );
            if (unifiedCap.wasCapped) {
                if (!options.suppressLimitDialog) {
                    showPromotionProductLimitDialog(product, quantity, finalPricingType);
                }
                quantity = unifiedCap.quantity;
            }
        }

        if (tabType !== 'get') {
            const lineStockCap = product ? getPromotionLineStockCap(product, finalPricingType) : null;
            if (lineStockCap !== null) {
                const capFloor = Math.max(0, Math.floor(lineStockCap));
                if (quantity > capFloor) {
                    if (!options.suppressLimitDialog) {
                        openStockLimitDialog(product, capFloor);
                    }
                    const cappedQty = capFloor;
                    lastUIQuantitiesRef.current[uniqueKey] = cappedQty;
                    setProductQuantities((prev) => ({
                        ...prev,
                        [uniqueKey]: cappedQty,
                    }));
                    if (tabType === 'buy' && product) {
                        const existingItemIndex = findPromotionCartLineIndex(cartItemLookup, {
                            productId,
                            orderType: finalPricingType,
                            tabType: 'buy',
                        });
                        const updatedCartItems = [...cartItems];
                        if (existingItemIndex !== -1) {
                            updatedCartItems[existingItemIndex].quantity = cappedQty;
                            updatedCartItems[existingItemIndex].qty = String(cappedQty);
                            updatedCartItems[existingItemIndex].allot_qty = String(cappedQty);
                        } else if (cappedQty > 0) {
                            updatedCartItems.push({
                                product_id: String(product.product_id),
                                product_code: product.product_code,
                                product_name: product.product_name,
                                quantity: cappedQty,
                                qty: String(cappedQty),
                                allot_qty: String(cappedQty),
                                unit_price: getProductPrice(product, finalPricingType),
                                type: finalPricingType,
                                piece_type: finalPricingType,
                                order_type: finalPricingType,
                                promotion_id: String(promotion.id),
                                promotion_applied: false,
                                promo_type: 'BUY',
                                customer_id: customerId,
                                sales_box_price: parseFloat(product?.box_sales_price || product?.sales_price || 0),
                                sales_piece_price: parseFloat(product?.piece_sales_price || product?.sales_price || 0),
                                split: parseInt(product?.split || 1),
                                rate: String(product?.vat_rate || product?.tax_rate || 0),
                                tax_rate: String(product?.vat_rate || product?.tax_rate || 0),
                                is_promoted: "0",
                                promosId: 0,
                            });
                        }
                        if (existingItemIndex !== -1 || cappedQty > 0) {
                            if (onCartUpdate) {
                                onCartUpdate(updatedCartItems, null);
                            }
                            if (onQueuePromotionCheck) {
                                onQueuePromotionCheck({
                                    productId,
                                    timestamp: Date.now(),
                                });
                            }
                        }
                    }
                    return false;
                }
            }
        }

        if (tabType === 'get' && isCompulsory) {
            const uiQty = Number(productQuantities[uniqueKey] || 0);
            if (quantity === 0 && (existingQty > 0 || uiQty > 0)) {
                setSnackbar({
                    open: true,
                    message: 'This is a compulsory product and cannot be removed',
                    severity: 'warning'
                });
                return false;
            }
            if (mandatoryMinQty > 0 && quantity > 0 && quantity < mandatoryMinQty) {
                setSnackbar({
                    open: true,
                    message: `Minimum quantity for this compulsory product is ${mandatoryMinQty}`,
                    severity: 'warning'
                });
                return false;
            }
        }

        if (tabType === 'get' && getQuantity > 0) {
            const maxAllowedGetQty = calculateMaxAllowedGetQuantity();
            const updatedQuantities = { ...productQuantities };
            updatedQuantities[uniqueKey] = quantity;
            
            let finalTotalQty = 0;
            Object.keys(existingCartQuantities)
                .filter(key => key.startsWith('get_') && !key.startsWith('buy_'))
                .forEach(key => {
                    const existingQty = existingCartQuantities[key] || 0;
                    const uiQty = updatedQuantities[key];
                    if (uiQty !== undefined && uiQty !== null) {
                        finalTotalQty += uiQty;
                    } else {
                        finalTotalQty += existingQty;
                    }
                });

            Object.keys(updatedQuantities)
                .filter(key => key.startsWith('get_') && !key.startsWith('buy_') && !existingCartQuantities[key])
                .forEach(key => {
                    finalTotalQty += updatedQuantities[key] || 0;
                });

            let currentTotal = Object.keys(existingCartQuantities)
                .filter(key => key.startsWith('get_') && !key.startsWith('buy_'))
                .reduce((sum, key) => {
                    const uiQty = productQuantities[key];
                    return sum + (uiQty !== undefined && uiQty !== null ? uiQty : existingCartQuantities[key] || 0);
                }, 0);
            
            currentTotal += Object.keys(productQuantities)
                .filter(key => key.startsWith('get_') && !key.startsWith('buy_') && !existingCartQuantities[key])
                .reduce((sum, key) => sum + (productQuantities[key] || 0), 0);

            const isIncreasing = finalTotalQty > currentTotal;
            
            if (isIncreasing && finalTotalQty > maxAllowedGetQty) {
                openMaxGetBlockedDialog(product);
                return false;
            }
        }

        lastUIQuantitiesRef.current[uniqueKey] = quantity;

        setProductQuantities(prev => {
            const newQuantities = { ...prev };
            newQuantities[uniqueKey] = quantity;
            return newQuantities;
        });

        if (tabType === 'buy') {
            const existingItemIndex = findPromotionCartLineIndex(cartItemLookup, {
                productId,
                orderType: finalPricingType,
                tabType: 'buy',
            });

            const updatedCartItems = [...cartItems];
            if (existingItemIndex !== -1) {
                updatedCartItems[existingItemIndex].quantity = quantity;
                updatedCartItems[existingItemIndex].qty = String(quantity);
                updatedCartItems[existingItemIndex].allot_qty = String(quantity);
            } else if (quantity > 0 && product) {
                const newItem = {
                    product_id: String(product.product_id),
                    product_code: product.product_code,
                    product_name: product.product_name,
                    quantity: quantity,
                    qty: String(quantity),
                    allot_qty: String(quantity),
                    unit_price: getProductPrice(product, finalPricingType),
                    type: finalPricingType,
                    piece_type: finalPricingType,
                    order_type: finalPricingType,
                    promotion_id: String(promotion.id),
                    promotion_applied: false,
                    promo_type: 'BUY',
                    customer_id: customerId,
                    sales_box_price: parseFloat(product?.box_sales_price || product?.sales_price || 0),
                    sales_piece_price: parseFloat(product?.piece_sales_price || product?.sales_price || 0),
                    split: parseInt(product?.split || 1),
                    rate: String(product?.vat_rate || product?.tax_rate || 0),
                    tax_rate: String(product?.vat_rate || product?.tax_rate || 0),
                    is_promoted: "0",
                    promosId: 0
                };
                updatedCartItems.push(newItem);
            }

            if (existingItemIndex !== -1 || (quantity > 0 && product)) {
                if (onCartUpdate) {
                    onCartUpdate(updatedCartItems, null);
                }
                if (onQueuePromotionCheck) {
                    onQueuePromotionCheck({
                        productId: productId,
                        timestamp: Date.now()
                    });
                }
            }
        } else if (tabType === 'get' && promotion?.id) {
            trackOfferProductChanges(product, quantity, finalPricingType, existingQty, isCompulsory);
        }
        return true;
    };

    const trackOfferProductChanges = (product, quantity, pricingType, existingQty, isCompulsory) => {
        if (quantity === 0 && existingQty > 0 && !isCompulsory) {
            setSwapData(prev => {
                const newData = { ...prev };
                const removedItem = {
                    product_id: product.product_id,
                    product_name: product.product_name,
                    product_code: product.product_code,
                    product_type: pricingType,
                    qty: existingQty > 0 ? existingQty : quantity,
                    new_product_unit_price: getProductPrice(product, pricingType)
                };
                
                const existingRemovedIndex = newData.removed.findIndex(
                    item => item.product_id === product.product_id && item.product_type === pricingType
                );
                
                if (existingRemovedIndex === -1) {
                    newData.removed.push(removedItem);
                } else {
                    newData.removed[existingRemovedIndex] = removedItem;
                }
                
                newData.added = newData.added.filter(
                    item => !(item.product_id === product.product_id && item.product_type === pricingType)
                );
                
                setPromotionSwapData(promotion.id, newData);
                return newData;
            });
        } else if (quantity > 0 && (existingQty === 0 || existingQty !== quantity)) {
            setSwapData(prev => {
                const newData = { ...prev };
                const addedItem = {
                    product_id: product.product_id,
                    product_name: product.product_name,
                    product_code: product.product_code,
                    product_type: pricingType,
                    qty: quantity,
                    new_product_unit_price: getProductPrice(product, pricingType)
                };
                
                const existingAddedIndex = newData.added.findIndex(
                    item => item.product_id === product.product_id && item.product_type === pricingType
                );
                
                if (existingAddedIndex === -1) {
                    newData.added.push(addedItem);
                } else {
                    newData.added[existingAddedIndex] = addedItem;
                }
                
                newData.removed = newData.removed.filter(
                    item => !(item.product_id === product.product_id && item.product_type === pricingType)
                );
                
                setPromotionSwapData(promotion.id, newData);
                return newData;
            });
        }
    };

    const handlePricingTypeChange = (uniqueKey, pricingType) => {
        setProductPricingTypes(prev => {
            const newTypes = { ...prev };
            newTypes[uniqueKey] = pricingType;
            return newTypes;
        });

        const [tabType, productId, oldPricingType] = uniqueKey.split('_');
        
        if (tabType === 'buy' && productId) {
            const quantity = productQuantities[uniqueKey] || 0;
            
            if (quantity > 0) {
                const existingItemIndex = findPromotionCartLineIndex(cartItemLookup, {
                    productId,
                    orderType: oldPricingType,
                    tabType: 'buy',
                });

                if (existingItemIndex !== -1) {
                    const updatedCartItems = [...cartItems];
                    updatedCartItems[existingItemIndex].type = pricingType;
                    updatedCartItems[existingItemIndex].piece_type = pricingType;
                    updatedCartItems[existingItemIndex].order_type = pricingType;
                    
                    const product = buyProductsWithLimits.find(
                        p => String(p.product_id) === String(productId)
                    );
                    
                    if (product) {
                        updatedCartItems[existingItemIndex].unit_price = getProductPrice(product, pricingType);
                    }

                    if (onCartUpdate) {
                        onCartUpdate(updatedCartItems, null);
                    }

                    if (onQueuePromotionCheck) {
                        onQueuePromotionCheck({ 
                            productId: productId, 
                            pricingType: pricingType,
                            timestamp: Date.now() 
                        });
                    }
                }
            }
        }
    };

    const handleAddToCart = (product, quantity, pricingType, tabType) => {
        if (!quantity || quantity === 0) {
            setSnackbar({
                open: true,
                message: 'Please enter a quantity',
                severity: 'warning'
            });
            return;
        }

        const isCompulsory = isCompulsoryOfferProduct(product);
        const mandatoryMinQty =
            tabType === 'get' && isCompulsory
                ? resolveMandatoryGetMinQuantity(
                      product,
                      promotionDetails,
                      existingCartQuantities,
                      productQuantities,
                      {
                          cartItems: cartLineItemsForLimits,
                          promotionIdStr: currentPromoId || '',
                      }
                  )
                : 0;
        
        if (tabType === 'get' && mandatoryMinQty > 0 && quantity < mandatoryMinQty) {
            setSnackbar({
                open: true,
                message: `Minimum quantity for this compulsory product is ${mandatoryMinQty}`,
                severity: 'warning'
            });
            return;
        }

        if (tabType === 'get' && shouldLockOfferProducts) {
            setShowInvalidProductsDialog(true);
            return;
        }

        const orderPricing = pricingType === 'Piece' || pricingType === 'piece' ? 'Piece' : 'Box';
        if (tabType !== 'get') {
            const addLineStockCap = product ? getPromotionLineStockCap(product, orderPricing) : null;
            if (addLineStockCap !== null) {
                const capFloor = Math.max(0, Math.floor(addLineStockCap));
                const targetQty =
                    typeof quantity === 'number' && Number.isFinite(quantity)
                        ? quantity
                        : (() => {
                              const n = parseFloat(quantity);
                              return Number.isFinite(n) ? n : 0;
                          })();
                if (targetQty > capFloor) {
                    openStockLimitDialog(product, capFloor);
                    return;
                }
            }
        }

        if (tabType === 'buy') {
            let targetQtyBuy =
                typeof quantity === 'number' && Number.isFinite(quantity)
                    ? quantity
                    : (() => {
                          const n = parseFloat(quantity);
                          return Number.isFinite(n) ? n : 0;
                      })();
            const existingLineForBuy = findPromotionCartLine(cartItemLookup, {
                productId: product.product_id,
                orderType: pricingType,
                tabType: 'buy',
            });
            const mergedBuy = mergeOrderLimitFieldsFromCartLines(
                existingLineForBuy ? { ...product, ...existingLineForBuy } : product,
                cartItems
            );
            const unifiedCap = clampToPromotionProductMax(
                product,
                cartItems,
                pricingType,
                targetQtyBuy,
                getPromotionLineStockCap
            );
            if (unifiedCap.wasCapped) {
                targetQtyBuy = unifiedCap.quantity;
            }
            const existingBuyQty = existingLineForBuy
                ? parseFloat(existingLineForBuy.qty ?? existingLineForBuy.quantity ?? 0) || 0
                : 0;
            const lineLimit = validateMaximumOrderQuantityForLineQty(
                {
                    ...mergedBuy,
                    product_id: product.product_id,
                    order_type: pricingType,
                    piece_type: pricingType,
                },
                cartItems,
                targetQtyBuy
            );
            if (!lineLimit.allowed) {
                const maxQty = getMaximumOrderQuantityFromItem(mergedBuy);
                if (maxQty != null && maxQty > 0) {
                    const otherQty =
                        getCartProductQuantityTotal(cartItems, product.product_id) - existingBuyQty;
                    targetQtyBuy = Math.max(
                        0,
                        Math.min(targetQtyBuy, Math.floor(maxQty - otherQty))
                    );
                }
            }
            quantity = targetQtyBuy;

            const existingItemIndex = findPromotionCartLineIndex(cartItemLookup, {
                productId: product.product_id,
                orderType: pricingType,
                tabType: 'buy',
            });

            const updatedCartItems = [...cartItems];
            
            if (existingItemIndex !== -1) {
                updatedCartItems[existingItemIndex].quantity = targetQtyBuy;
                updatedCartItems[existingItemIndex].qty = String(targetQtyBuy);
                updatedCartItems[existingItemIndex].allot_qty = String(targetQtyBuy);
            } else {
                const newItem = {
                    product_id: String(product.product_id),
                    product_code: product.product_code,
                    product_name: product.product_name,
                    quantity: quantity,
                    qty: String(quantity),
                    allot_qty: String(quantity),
                    unit_price: getProductPrice(product, pricingType),
                    type: pricingType,
                    piece_type: pricingType,
                    order_type: pricingType,
                    promotion_id: String(promotion.id),
                    promotion_applied: false,
                    promo_type: 'BUY',
                    customer_id: customerId,
                    sales_box_price: parseFloat(product?.box_sales_price || product?.sales_price || 0),
                    sales_piece_price: parseFloat(product?.piece_sales_price || product?.sales_price || 0),
                    split: parseInt(product?.split || 1),
                    rate: String(product?.vat_rate || product?.tax_rate || 0),
                    tax_rate: String(product?.vat_rate || product?.tax_rate || 0),
                    is_promoted: "0",
                    promosId: 0
                };
                updatedCartItems.push(newItem);
            }

            if (onCartUpdate) {
                onCartUpdate(updatedCartItems, null);
            }

            if (onQueuePromotionCheck) {
                onQueuePromotionCheck({ 
                    productId: product.product_id, 
                    timestamp: Date.now() 
                });
            }

            setSnackbar({
                open: true,
                message: 'Product added to cart',
                severity: 'success'
            });
        } else {
            handleQuantityChange(product.product_id, quantity, tabType, pricingType);
        }
    };

    const handleSubmitSwap = async () => {
        if (!promotion?.id || !customerId) {
            setShowSwapErrorDialog(true);
            setSwapErrorMessage('Missing promotion or customer. Please try again.');
            return;
        }

        // Match usePromotionDrawer: include every current GET line for this promotion in the payload,
        // not only rows in swapData.added. Otherwise replacing one free item drops other GET lines
        // and the API validates total GET qty against buy sets (e.g. 2 free lines �?å�? wrong "min 2" error).
        const allNewProducts = [...swapData.added];

        Object.keys(existingCartQuantities)
            .filter((key) => key.startsWith('get_') && !key.startsWith('buy_'))
            .forEach((key) => {
                const keyParts = key.split('_');
                const productId = keyParts[1];
                const orderType = keyParts[2] || 'Box';
                const existingQty = existingCartQuantities[key] || 0;

                if (existingQty > 0) {
                    const product = (promotionDetails.get_products || []).find(
                        (p) => String(p.product_id) === String(productId)
                    );
                    if (product) {
                        const pricingType = productPricingTypes[key] || orderType;
                        const isRemoved = swapData.removed.find(
                            (item) =>
                                String(item.product_id) === String(productId) &&
                                item.product_type === pricingType
                        );
                        const existingInAdded = allNewProducts.find(
                            (item) =>
                                String(item.product_id) === String(productId) &&
                                item.product_type === pricingType
                        );

                        if (!isRemoved && !existingInAdded) {
                            const currentQty = productQuantities[key] || existingQty;
                            allNewProducts.push({
                                product_id: product.product_id,
                                product_name: product.product_name,
                                product_code: product.product_code,
                                product_type: pricingType,
                                qty: currentQty,
                                new_product_unit_price: getProductPrice(product, pricingType),
                            });
                        }
                    }
                }
            });

        const removedPayload = swapData.removed.map((p) => {
            const productType = p.product_type;
            const key = `get_${p.product_id}_${productType}`;
            const cartQty = existingCartQuantities[key] || 0;
            const removeQty =
                cartQty > 0
                    ? cartQty
                    : p.qty !== undefined && p.qty !== null
                      ? p.qty
                      : 0;
            return {
                product_id: p.product_id,
                product_type: productType,
                qty: removeQty,
            };
        });

        const isAlreadyMarkedRemoved = (productId, productType) =>
            removedPayload.some(
                (r) =>
                    String(r.product_id) === String(productId) &&
                    String(r.product_type) === String(productType)
            );

        allNewProducts.forEach((newItem) => {
            const productId = newItem.product_id;
            const productType = newItem.product_type;
            const key = `get_${productId}_${productType}`;
            const existingQty = existingCartQuantities[key] || 0;
            if (existingQty > 0 && !isAlreadyMarkedRemoved(productId, productType)) {
                removedPayload.push({
                    product_id: productId,
                    product_type: productType,
                    qty: existingQty,
                });
            }
        });

        const newPayload = allNewProducts.map(p => ({
            product_id: p.product_id,
            product_type: p.product_type,
            qty: p.qty,
            new_product_unit_price: p.new_product_unit_price
        }));
        setIsSubmittingSwap(true);
        try {
            if (typeof onCheckPromotionGetSwap !== 'function') {
                setShowConfirmDialog(false);
                setSwapErrorMessage('Promotion swap is not configured for this screen.');
                setShowSwapErrorDialog(true);
                return;
            }

            const response = await onCheckPromotionGetSwap({
                promotionId: promotion.id,
                removed: removedPayload,
                newProducts: newPayload,
            });
            if (response?.success) {
                setShowConfirmDialog(false);
                clearPromotionSwapData(promotion.id);
                setSwapData({ removed: [], added: [] });
                setSnackbar({
                    open: true,
                    message: 'Promotional products swapped successfully',
                    severity: 'success'
                });
            } else {
                setShowConfirmDialog(false);
                const errorMessage = response?.message || response?.data?.message || response?.error || 'Failed to swap promotional products';
                setSwapErrorMessage(errorMessage.toLowerCase().includes('new_product_id is required') ? 'Please add promotional Products' : errorMessage);
                setShowSwapErrorDialog(true);
            }
        } catch (err) {
            setShowConfirmDialog(false);
            const errorMessage = err?.message || err?.response?.data?.message || 'Failed to swap promotional products. Please try again.';
            setSwapErrorMessage(errorMessage.toLowerCase().includes('new_product_id is required') ? 'Please add promotional Products' : errorMessage);
            setShowSwapErrorDialog(true);
        } finally {
            setIsSubmittingSwap(false);
        }
    };

    const handleCloseDrawer = () => {
        const missing = getMissingCompulsoryProductCodes(
            promotionDetails,
            productQuantities,
            existingCartQuantities
        );
        if (missing.length > 0) {
            if (hasAcknowledgedCompulsoryDialog) {
                onClose();
                return;
            }
            setCompulsoryDialogCodes(missing);
            setShowCompulsoryDialog(true);
            return;
        }
        if (promotion?.id) {
            const savedSwapData = getPromotionSwapData(promotion.id);
            if (savedSwapData.removed.length > 0 || savedSwapData.added.length > 0) {
                setShowDiscardDialog(true);
                return;
            }
        }
        onClose();
    };

    const handleCloseCompulsoryDialog = () => {
        setShowCompulsoryDialog(false);
        setHasAcknowledgedCompulsoryDialog(true);
    };

    const handleDiscardChanges = () => {
        if (promotion?.id) {
            clearPromotionSwapData(promotion.id);
            setSwapData({ removed: [], added: [] });
        }
        setShowDiscardDialog(false);
        onClose();
    };

    const sortCompulsoryFirst = (a, b) => {
        const aComp = isCompulsoryOfferProduct(a) ? 1 : 0;
        const bComp = isCompulsoryOfferProduct(b) ? 1 : 0;
        return bComp - aComp;
    };

    const filteredBuyProducts = (() => {
        const searchFiltered = buyProductsWithLimits.filter(
            (product) =>
                (product?.product_name?.toLowerCase()?.includes(searchTerm.toLowerCase()) || false) ||
                (product?.product_code?.includes(searchTerm) || false)
        );
        const groupFiltered =
            buyProductGroupIds.length > 1 && selectedBuyGroupId != null
                ? searchFiltered.filter(
                      (p) => String(resolveProductGroupId(p)) === String(selectedBuyGroupId)
                  )
                : searchFiltered;
        return groupFiltered.sort(sortCompulsoryFirst);
    })();

    const filteredOfferProducts = (() => {
        const searchFiltered = (promotionDetails.get_products || []).filter(
            (product) =>
                (product?.product_name?.toLowerCase()?.includes(searchTerm.toLowerCase()) || false) ||
                (product?.product_code?.includes(searchTerm) || false)
        );
        const groupFiltered =
            getProductGroupIds.length > 1 && selectedGetGroupId != null
                ? searchFiltered.filter(
                      (p) => String(resolveProductGroupId(p)) === String(selectedGetGroupId)
                  )
                : searchFiltered;
        return groupFiltered.sort(sortCompulsoryFirst);
    })();

    return {
        activeTab,
        searchTerm,
        productQuantities,
        productPricingTypes,
        existingCartQuantities,
        snackbar,
        limitDialog,
        closeLimitDialog,
        swapData,
        showConfirmDialog,
        isSubmittingSwap,
        showDiscardDialog,
        promotionDetails,
        isLoading,
        error,
        hasGetProducts,
        selectedBuyProductsCount,
        selectedGetProductsCount,
        hasOtherPromotionsInCart: shouldLockOfferProducts,
        filteredBuyProducts,
        filteredOfferProducts,
        buyProductGroupIds,
        selectedBuyGroupId,
        setSelectedBuyGroupId,
        getProductGroupIds,
        selectedGetGroupId,
        setSelectedGetGroupId,
        setSearchTerm,
        handleTabChange,
        handleQuantityChange,
        notifyQuantityLimitExceeded,
        handlePricingTypeChange,
        handleAddToCart,
        handleSubmitSwap,
        handleCloseDrawer,
        handleDiscardChanges,
        setShowConfirmDialog,
        setShowDiscardDialog,
        showInvalidProductsDialog,
        setShowInvalidProductsDialog,
        showSwapErrorDialog,
        setShowSwapErrorDialog,
        swapErrorMessage,
        setSnackbar,
        showCompulsoryDialog,
        setShowCompulsoryDialog,
        compulsoryDialogCodes,
        handleCloseCompulsoryDialog
    };
};
